import { createSlice } from "@reduxjs/toolkit";
import Config from "../../utils/Config";

function writeTokenToStorage(token) {
  try {
    if (token) localStorage.setItem(Config.TOKEN_KEY, String(token));
    else localStorage.removeItem(Config.TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

const initialState = {
  isAuthenticated: false,
  token: null,
  user: null,
  registrationStatus: "idle",
  registrationError: null,
  loginStatus: "idle",
  loginError: null,
  verifyStatus: "idle",
  verifyError: null,
  profileUpdateStatus: "idle",
  profileUpdateError: null,
  /** Set after successful register (no token yet); cleared after successful OTP verify */
  pendingVerification: null,
};

export const userSlice = createSlice({
  name: "infoboxx_consumer",
  initialState,
  reducers: {
    /**
     * Reset auth flow UI state (status/error/pending verification), but keep
     * active session data if the user is already authenticated.
     */
    resetAuthFlowState: (state) => {
      state.registrationStatus = "idle";
      state.registrationError = null;
      state.loginStatus = "idle";
      state.loginError = null;
      state.verifyStatus = "idle";
      state.verifyError = null;
      state.pendingVerification = null;
    },
    registerStart: (state) => {
      state.registrationStatus = "loading";
      state.registrationError = null;
    },
    registerSuccess: (state, action) => {
      state.registrationStatus = "succeeded";
      state.registrationError = null;
      const row = action.payload || {};
      state.pendingVerification = {
        email: row.email,
        phone_number: row.phone_number,
        first_name: row.first_name,
        last_name: row.last_name,
        location: row.location,
      };
    },
    registerFailure: (state, action) => {
      state.registrationStatus = "failed";
      state.registrationError =
        typeof action.payload === "string" ? action.payload : "Registration failed";
    },
    loginStart: (state) => {
      state.loginStatus = "loading";
      state.loginError = null;
    },
    /**
     * Handles both login-with-token and login-OTP-init flows.
     */
    loginSuccess: (state, action) => {
      state.loginStatus = "succeeded";
      state.loginError = null;
      const payload = action.payload || {};
      const data = payload.data;
      const dataObj =
        data && typeof data === "object" && !Array.isArray(data) ? data : null;

      const token =
        payload.token ??
        payload.access_token ??
        dataObj?.token ??
        dataObj?.access_token ??
        null;

      let user = payload.user ?? dataObj?.user ?? null;
      if (!user && dataObj) {
        const {
          token: _tok,
          access_token: _at,
          user: nestedUser,
          ...profile
        } = dataObj;
        user =
          nestedUser && typeof nestedUser === "object"
            ? nestedUser
            : Object.keys(profile).length
              ? profile
              : null;
      }

      if (token) {
        state.token = token;
        writeTokenToStorage(token);
        state.user = user ?? state.user;
        state.isAuthenticated = true;
        state.pendingVerification = null;
        return;
      }

      const email = payload.email || "";
      state.pendingVerification = {
        email,
        phone_number: null,
        first_name: null,
        last_name: null,
        location: null,
      };
    },
    loginFailure: (state, action) => {
      state.loginStatus = "failed";
      state.loginError =
        typeof action.payload === "string" ? action.payload : "Could not send OTP";
    },
    clearLoginError: (state) => {
      state.loginError = null;
    },
    verifyStart: (state) => {
      state.verifyStatus = "loading";
      state.verifyError = null;
    },
    verifySuccess: (state, action) => {
      state.verifyStatus = "succeeded";
      state.verifyError = null;
      state.pendingVerification = null;

      const p = action.payload || {};
      const token = p.token ?? p.access_token ?? null;
      const { token: _t, access_token: _a, ...profile } = p;

      state.user = {
        ...profile,
        email_verified_at:
          profile.email_verified_at ?? new Date().toISOString(),
      };

      if (token) {
        state.token = token;
        writeTokenToStorage(token);
      }

      state.isAuthenticated = !!state.token;
    },
    verifyFailure: (state, action) => {
      state.verifyStatus = "failed";
      state.verifyError =
        typeof action.payload === "string" ? action.payload : "Verification failed";
    },
    profileUpdateStart: (state) => {
      state.profileUpdateStatus = "loading";
      state.profileUpdateError = null;
    },
    profileUpdateSuccess: (state, action) => {
      state.profileUpdateStatus = "succeeded";
      state.profileUpdateError = null;
      state.user = { ...state.user, ...(action.payload || {}) };
    },
    profileUpdateFailure: (state, action) => {
      state.profileUpdateStatus = "failed";
      state.profileUpdateError =
        typeof action.payload === "string" ? action.payload : "Profile update failed";
    },
    setUser: (state, action) => {
      const payload = action.payload;
      const user = payload?.user ?? payload;
      const token = payload?.token ?? null;
      state.user = user;
      if (token !== undefined && token !== null && token !== "") {
        state.token = token;
        writeTokenToStorage(token);
      }
      state.isAuthenticated = !!state.token;
    },
    setToken: (state, action) => {
      state.token = action.payload ?? null;
      writeTokenToStorage(state.token);
      state.isAuthenticated = !!state.token;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.pendingVerification = null;
      state.registrationStatus = "idle";
      state.registrationError = null;
      state.loginStatus = "idle";
      state.loginError = null;
      state.verifyStatus = "idle";
      state.verifyError = null;
      state.profileUpdateStatus = "idle";
      state.profileUpdateError = null;
      writeTokenToStorage(null);
    },
    updateProfile: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    /**
     * Append a resolved interest row after subscribing to a sub-topic on-site.
     * Shape aligns with login payload `user.interests[]`.
     */
    addUserInterest: (state, action) => {
      const row = action.payload;
      if (!state.user || !row || typeof row !== "object") return;
      const prev = Array.isArray(state.user.interests)
        ? state.user.interests
        : [];
      state.user = {
        ...state.user,
        interests: [...prev, row],
      };
    },
  },
  extraReducers: () => {},
});

export const {
  setUser,
  setToken,
  logout,
  updateProfile,
  resetAuthFlowState,
  registerStart,
  registerSuccess,
  registerFailure,
  loginStart,
  loginSuccess,
  loginFailure,
  clearLoginError,
  verifyStart,
  verifySuccess,
  verifyFailure,
  profileUpdateStart,
  profileUpdateSuccess,
  profileUpdateFailure,
  addUserInterest,
} = userSlice.actions;
export default userSlice.reducer;
